/**
 * Workflow Step Indicator Component
 *
 * Displays a horizontal step progress indicator showing which workflow
 * steps are complete/incomplete. Used within WorkflowCard and
 * WorkflowDashboard to visualize per-resource onboarding progress.
 *
 * @module modules/workflows/components
 * @spec 135-ui-data-alignment
 * @story US2 - Workflow Guidance
 * @constitution v1.12.0 - Principle XXXVII (Code Header Standards)
 */

import React from 'react';
import { Check } from 'lucide-react';
import type { WorkflowStepStatus } from '../../../shared/types/workflow';
import { AppTooltip } from '../../../components/ui/AppTooltip';

// =============================================================================
// PROPS
// =============================================================================

export interface WorkflowStepIndicatorProps {
  /** Array of step statuses to display */
  steps: WorkflowStepStatus[];
  /** Current active step number (first incomplete step) */
  currentStep: number;
  /** Whether to show step labels (default: true) */
  showLabels?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * WorkflowStepIndicator — Visual step progress bar for workflow tracking.
 *
 * Renders a horizontal row of step circles connected by lines.
 * Completed steps show a checkmark, the current step is highlighted,
 * and future steps are dimmed.
 */
export const WorkflowStepIndicator: React.FC<WorkflowStepIndicatorProps> = ({
  steps,
  currentStep,
  showLabels = true,
  size = 'md',
}) => {
  const circleSize = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  const fontSize = size === 'sm' ? 'text-[10px]' : 'text-xs';

  return (
    <div className="flex items-start w-full" role="list" aria-label="Workflow steps">
      {steps.map((step, index) => {
        const isComplete = step.isComplete;
        const isCurrent = step.stepNumber === currentStep;
        const isLast = index === steps.length - 1;

        return (
          <div
            key={step.stepNumber}
            className={`flex items-start ${isLast ? '' : 'flex-1'}`}
            role="listitem"
            aria-label={`Step ${step.stepNumber}: ${step.name} — ${isComplete ? 'Complete' : isCurrent ? 'Current' : 'Pending'}`}
          >
            <div className="flex flex-col items-center">
              {/* Step Circle (T090) */}
              <AppTooltip
                title={`${step.name} — ${isComplete ? 'Complete' : isCurrent ? 'Current step' : 'Pending'}`}
                placement='top'
              >
                <div
                  className={`
                    ${circleSize} rounded-full flex items-center justify-center text-xs font-medium cursor-help
                    ${isComplete
                      ? 'bg-green-500 text-white'
                      : isCurrent
                        ? 'bg-blue-500 text-white ring-2 ring-blue-300 dark:ring-blue-700'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }
                  `}
                >
                  {isComplete ? (
                    <Check className={iconSize} aria-hidden="true" />
                  ) : (
                    step.stepNumber
                  )}
                </div>
              </AppTooltip>

              {/* Step Label */}
              {showLabels && (
                <span
                  className={`
                    mt-1 ${fontSize} text-center max-w-24 leading-tight
                    ${isComplete
                      ? 'text-green-700 dark:text-green-400 font-medium'
                      : isCurrent
                        ? 'text-blue-700 dark:text-blue-400 font-medium'
                        : 'text-gray-500 dark:text-gray-400'
                    }
                  `}
                >
                  {step.name}
                </span>
              )}
            </div>

            {/* Connector Line */}
            {!isLast && (
              <div
                className={`
                  flex-1 h-0.5 mt-${size === 'sm' ? '3' : '4'} mx-1
                  ${isComplete
                    ? 'bg-green-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                  }
                `}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowStepIndicator;
